'use client';

import { useState, useEffect, FormEvent } from 'react';
import { trackLead } from '@/lib/tracking';

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18"/>
    <path d="m6 6 12 12"/>
  </svg>
);

const MessageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14"/>
    <path d="m12 5 7 7-7 7"/>
  </svg>
);

const LoadingSpinner = () => (
  <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  consent?: string;
}

export default function MessageButton() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false,
  });
  const [honeypot, setHoneypot] = useState('');
  const [formLoadedAt] = useState(Date.now());
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isFormOpen) {
        setIsNotificationVisible(true);
      }
    }, 6000);

    return () => clearTimeout(timer);
  }, [isFormOpen]);

  const validateField = (name: string, value: string | boolean): string | undefined => {
    switch (name) {
      case 'name':
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          return 'Jméno musí mít alespoň 2 znaky';
        }
        break;
      case 'email':
        if (!value || (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
          return 'Zadejte platný e-mail';
        }
        break;
      case 'message':
        if (!value || (typeof value === 'string' && value.trim().length < 10)) {
          return 'Zpráva musí mít alespoň 10 znaků';
        }
        break;
      case 'consent':
        if (!value) {
          return 'Musíte souhlasit se zpracováním údajů';
        }
        break;
    }
    return undefined;
  };

  const handleBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    (Object.keys(formData) as Array<keyof typeof formData>).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key as keyof FormErrors] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, message: true, consent: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'message-button', website: honeypot, _t: formLoadedAt }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackLead({ source: 'message-button', name: formData.name, email: formData.email });
        setFormData({ name: '', email: '', phone: '', message: '', consent: false });
        setTouched({});
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const toggleForm = () => {
    if (isFormOpen) {
      setIsFormOpen(false);
    } else {
      setIsNotificationVisible(false);
      setIsFormOpen(true);
      setSubmitStatus('idle');
    }
  };

  const closeNotification = () => {
    setIsNotificationVisible(false);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <div className="message-button-container">
      <div id="message-form" className={isFormOpen ? 'visible' : ''}>
        <div className="message-form-header">
          <h2>Napište mi</h2>
          <button className="close-btn" onClick={closeForm} aria-label="Zavřít">
            <CloseIcon />
          </button>
        </div>

        {submitStatus === 'success' ? (
          <div className="message-form-success">
            <div className="success-check">
              <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="26" cy="26" r="25" stroke="#4001FF" strokeWidth="2"/>
                <path d="M15 27l7 7 15-15" stroke="#4001FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Děkuji za zprávu!</h3>
            <p>Ozvu se Vám co nejdříve.</p>
            <button className="message-submit-btn" onClick={() => setSubmitStatus('idle')}>
              <span>Odeslat další</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="message-form-content" noValidate>
            <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{ position: 'absolute', left: '-9999px', tabIndex: -1 } as React.CSSProperties} autoComplete="off" aria-hidden="true" />
            <div className={`msg-form-group ${errors.name && touched.name ? 'has-error' : ''}`}>
              <label htmlFor="msg-name">Jméno *</label>
              <input
                type="text"
                id="msg-name"
                placeholder="Jan Novák"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
              />
              {errors.name && touched.name && <span className="msg-error">{errors.name}</span>}
            </div>

            <div className={`msg-form-group ${errors.email && touched.email ? 'has-error' : ''}`}>
              <label htmlFor="msg-email">E-mail *</label>
              <input
                type="email"
                id="msg-email"
                placeholder="jan@example.cz"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
              />
              {errors.email && touched.email && <span className="msg-error">{errors.email}</span>}
            </div>

            <div className="msg-form-group">
              <label htmlFor="msg-phone">Telefon <span className="optional">(nepovinné)</span></label>
              <input
                type="tel"
                id="msg-phone"
                placeholder="+420 777 888 999"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </div>

            <div className={`msg-form-group ${errors.message && touched.message ? 'has-error' : ''}`}>
              <label htmlFor="msg-message">Zpráva *</label>
              <textarea
                id="msg-message"
                placeholder="Popište svůj projekt..."
                rows={4}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                onBlur={() => handleBlur('message')}
              />
              {errors.message && touched.message && <span className="msg-error">{errors.message}</span>}
            </div>

            <div className={`msg-form-check ${errors.consent && touched.consent ? 'has-error' : ''}`}>
              <label className="msg-checkbox-container">
                <input
                  type="checkbox"
                  checked={formData.consent}
                  onChange={(e) => handleChange('consent', e.target.checked)}
                  onBlur={() => handleBlur('consent')}
                />
                <span className="msg-checkbox-mark"></span>
                <span className="msg-checkbox-text">Souhlasím se zpracováním údajů *</span>
              </label>
              {errors.consent && touched.consent && <span className="msg-error">{errors.consent}</span>}
            </div>

            {submitStatus === 'error' && (
              <div className="msg-form-error">
                <p>Něco se pokazilo. Zkuste to znovu.</p>
              </div>
            )}

            <button className="message-submit-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoadingSpinner />
                  <span>Odesílám...</span>
                </>
              ) : (
                <>
                  <span>Odeslat zprávu</span>
                  <div className="btn-icon">
                    <ArrowRightIcon />
                  </div>
                </>
              )}
            </button>
          </form>
        )}
      </div>

      <div className={`notification ${isNotificationVisible ? 'visible' : ''}`}>
        <button className="notif-close" onClick={closeNotification} aria-label="Zavřít">
          <CloseIcon />
        </button>
        <p>Dobrý den! 👋 Potřebujete poradit? Klikněte a zanechte mi kontakt.</p>
      </div>

      <button id="messageBtn" className="message-button" onClick={toggleForm} aria-label="Napsat zprávu">
        <MessageIcon />
      </button>
    </div>
  );
}
